import { MapContainer, TileLayer, Marker, Circle, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icon (same approach as MapPicker)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * Read-only Leaflet map for displaying a single business or pickup location.
 *
 * Props:
 *   lat, lng     – numbers (required)
 *   label        – string shown in marker popup
 *   approximate  – when true, replaces pin with a translucent circle (~1km radius)
 *   height       – CSS height (default 280px)
 *   zoom         – default 13 (will be reduced to 11 in approximate mode)
 */
export default function MapDisplay({
  lat,
  lng,
  label,
  approximate = false,
  height = '280px',
  zoom,
  className = '',
}) {
  if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
    return null;
  }
  const center = { lat: Number(lat), lng: Number(lng) };
  const z = zoom ?? (approximate ? 12 : 14);

  return (
    // `isolate` contains Leaflet's own internal pane z-indexes (tiles/markers/
    // popups go up to ~700), which otherwise aren't bounded by any stacking
    // context here and can paint over a much higher page-level overlay (e.g.
    // the renter walkthrough's spotlight dimming layer) even though the
    // overlay's own z-index is far higher on paper.
    <div className={`relative isolate rounded-xl overflow-hidden border border-slate-200 ${className}`}
         data-testid="map-display">
      <MapContainer
        center={center}
        zoom={z}
        scrollWheelZoom={false}
        style={{ height, width: '100%' }}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {approximate ? (
          <Circle
            center={center}
            radius={1000}
            pathOptions={{ color: '#1B4332', fillColor: '#1B4332', fillOpacity: 0.18, weight: 1.5 }}
          >
            {label && <Popup>{label} (approximate area)</Popup>}
          </Circle>
        ) : (
          <Marker position={center}>
            {label && <Popup>{label}</Popup>}
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
